<div class="bai-page-shell" aria-label="Client Financial Health Dashboard">

  <!-- ── Skeleton loader ──────────────────────────────────────────────── -->
  @if (loading()) {
    <div class="grid grid-cols-2 lg:grid-cols-4 gap-4">
      @for (_ of [1,2,3,4]; track $index) {
        <div class="bg-white rounded border border-gray-200 p-4">
          <p-skeleton height="1rem" styleClass="mb-2" />
          <p-skeleton height="2rem" width="60%" />
        </div>
      }
    </div>
    <div class="bg-white rounded border border-gray-200 p-4">
      <p-skeleton height="1.5rem" width="30%" styleClass="mb-4" />
      <p-skeleton height="40px" />
    </div>
    <div class="bg-white rounded border border-gray-200 p-4">
      <p-skeleton height="1.5rem" width="40%" styleClass="mb-4" />
      <p-skeleton height="120px" />
    </div>
  }

  <!-- ── No client selected ───────────────────────────────────────────── -->
  @else if (!state()) {
    <div class="flex flex-col items-center justify-center py-20 text-gray-400" role="status">
      <i class="pi pi-user text-5xl mb-4"></i>
      <p class="text-lg font-medium">No client selected</p>
      <p class="text-sm">Select a client from the list to view their financial health dashboard.</p>
    </div>
  }

  <!-- ── Dashboard ────────────────────────────────────────────────────── -->
  @else {
    <!-- Row 1: AI Priority Brief -->
    <!-- <section class="bai-card bai-priority-brief" aria-label="AI priority brief">
      <div class="bai-priority-brief__header">
        <div>
          <h2 class="bai-card__title">
            <i class="pi pi-sparkles mr-2 text-amber-500"></i>AI Priority Brief
          </h2>
          <p class="bai-priority-brief__subtitle">
            Insights ranked by urgency and impact. Priority <strong>#1</strong> is the first recommended focus.
          </p>
        </div>
        <div class="bai-priority-brief__stats">
          @if (highRiskDebtCount(state()!) > 0) {
            <p-tag [value]="highRiskDebtCount(state()!) + ' high-risk debt'" severity="danger" styleClass="text-xs" />
          }
          @if (urgentBillsCount(state()!) > 0) {
            <p-tag [value]="urgentBillsCount(state()!) + ' urgent bill'" severity="warn" styleClass="text-xs" />
          }
        </div>
      </div>
      <div class="bai-priority-brief__grid grid grid-cols-1 lg:grid-cols-2 gap-3 mt-4">
        @for (priorityItem of prioritizedInsights(); track trackInsight($index, priorityItem.insight)) {
          @let insight = priorityItem.insight;
          <article class="bai-insight" [class]="insightSeverityClass(insight.severity)" role="article">
            <div class="bai-insight__header">
              <span class="bai-insight__rank" aria-hidden="true">{{ priorityItem.rank }}</span>
              <div class="bai-insight__content">
                <div class="bai-insight__title-row">
                  <i [class]="insight.icon + ' bai-insight__icon'"></i>
                  <span class="bai-insight__title">{{ insight.title }}</span>
                  <span class="bai-insight__priority">{{ insightPriorityLabel(insight.severity) }}</span>
                </div>
                <p class="bai-insight__body">{{ insight.body }}</p>
              </div>
              <details class="bai-fin-kebab" (click)="$event.stopPropagation(); $any($event.target).closest('[class*=__action]') && $any($event.currentTarget).removeAttribute('open')">
                <summary class="bai-fin-kebab__btn" aria-label="Insight options">
                  <i class="pi pi-bars" aria-hidden="true"></i>
                </summary>
                <button
                  type="button"
                  class="bai-fin-kebab__action"
                  (click)="addInsightToCopilot(insight)"
                >
                  Add to Copilot
                </button>
              </details>
            </div>
            <div class="bai-insight__footer">
              <div class="bai-insight__action">
                <i class="pi pi-arrow-right text-xs mr-1"></i>
                <span>{{ insight.suggestedAction }}</span>
              </div>
              <button type="button" class="bai-insight__btn">{{ insightActionLabel(insight.severity) }}</button>
            </div>
          </article>
        }
      </div>
    </section> -->

    <!-- Row 2: Health at a glance -->
    <section aria-label="Financial summary at a glance">
      <h2 class="bai-fin-section-heading">Health at a Glance</h2>
      <div class="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div class="bai-summary-card">
          <span class="bai-summary-card__label">Credit Score</span>
          <span class="bai-summary-card__value">{{ state()!.creditHealth.score }}</span>
          <p-tag
            [value]="state()!.creditHealth.band"
            [severity]="bandSeverity(state()!.creditHealth.band)"
            styleClass="mt-1 text-xs"
          />
        </div>
        <div class="bai-summary-card">
          <span class="bai-summary-card__label">Debt-to-Income</span>
          <span class="bai-summary-card__value">{{ formatDti(state()!.dti) }}</span>
          <p-tag
            [value]="dtiRiskLabel(state()!.dti)"
            [severity]="dtiRiskSeverity(state()!.dti)"
            styleClass="mt-1 text-xs"
          />
        </div>
        <div class="bai-summary-card">
          <span class="bai-summary-card__label">Total Debt</span>
          <span class="bai-summary-card__value">{{ formatUsd(totalDebt(state()!.debts)) }}</span>
          <span class="bai-summary-card__sub">{{ state()!.debts.length }} account{{ state()!.debts.length !== 1 ? 's' : '' }}</span>
        </div>
        <div class="bai-summary-card">
          <span class="bai-summary-card__label">Emergency Fund</span>
          <span class="bai-summary-card__value">{{ formatUsd(state()!.emergencySavings) }}</span>
          <span class="bai-summary-card__sub">{{ emergencyMonths(state()!).toFixed(1) }} months coverage</span>
        </div>
      </div>

      <div class="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
        <div class="bai-card bai-health-panel">
          <div class="bai-card__header">
            <h3 class="bai-card__title">
              <i class="pi pi-chart-line mr-2"></i>Credit Momentum
            </h3>
            <span class="text-xs text-gray-400">Updated {{ state()!.creditHealth.lastUpdated }}</span>
          </div>
          <div class="mt-3">
            <svg
              class="bai-sparkline"
              viewBox="0 0 200 40"
              preserveAspectRatio="none"
              role="img"
              aria-label="12-month credit score trend chart"
            >
              <polyline
                [attr.points]="sparklinePoints(state()!)"
                fill="none"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linejoin="round"
                stroke-linecap="round"
              />
            </svg>
            <div class="flex justify-between text-xs text-gray-400 mt-1">
              <span>{{ state()!.creditHealth.trend[0].month }}</span>
              <span>{{ state()!.creditHealth.trend[11].month }}</span>
            </div>
          </div>
        </div>

        <div class="bai-card bai-health-panel">
          <div class="bai-card__header">
            <h3 class="bai-card__title">
              <i class="pi pi-bolt mr-2"></i>Immediate Watchlist
            </h3>
          </div>
          <div class="mt-3 flex flex-col gap-3 text-sm">
            <div class="flex items-center justify-between">
              <span class="text-gray-600">Credit utilization</span>
              <span class="font-semibold text-gray-800">{{ state()!.creditHealth.utilizationPct }}%</span>
            </div>
            <div class="flex items-center justify-between">
              <span class="text-gray-600">Debt-to-income</span>
              <span class="font-semibold text-gray-800">{{ formatDti(state()!.dti) }}</span>
            </div>
            <div class="flex items-center justify-between">
              <span class="text-gray-600">Urgent bills</span>
              <span class="font-semibold text-gray-800">{{ urgentBillsCount(state()!) }}</span>
            </div>
            <p class="bai-health-panel__hint">
              Open the detail modules below for full context and next-step execution.
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- Row 3+: Progressive detail modules -->
    <section class="bai-detail-stack" aria-label="Financial details">
      <section class="bai-card bai-detail-card" aria-label="Credit health">
        <button
          type="button"
          class="bai-detail-toggle"
          (click)="toggleSection('credit')"
          [attr.aria-expanded]="creditDetailsOpen()"
        >
          <span class="bai-detail-toggle__title"><i class="pi pi-credit-card mr-2"></i>Credit Health</span>
          <span class="bai-detail-toggle__meta">Updated {{ state()!.creditHealth.lastUpdated }}</span>
          <i class="pi bai-detail-toggle__chevron" [class.pi-chevron-down]="creditDetailsOpen()" [class.pi-chevron-right]="!creditDetailsOpen()"></i>
        </button>

        @if (creditDetailsOpen()) {
          <div class="grid grid-cols-1 md:grid-cols-3 gap-6 mt-4">
            <div class="flex flex-col gap-3">
              <div class="flex items-center gap-3">
                <span class="bai-score-circle" [attr.data-band]="state()!.creditHealth.band">{{ state()!.creditHealth.score }}</span>
                <div class="flex flex-col">
                  <p-tag [value]="state()!.creditHealth.band" [severity]="bandSeverity(state()!.creditHealth.band)" />
                  <span class="text-xs text-gray-500 mt-1">300 – 850 range</span>
                </div>
              </div>
              <div class="flex flex-col gap-1">
                <div class="flex justify-between text-xs text-gray-600 mb-0.5">
                  <span>Utilization</span>
                  <span>{{ state()!.creditHealth.utilizationPct }}%</span>
                </div>
                <div class="bai-progress-bar" role="progressbar" [attr.aria-valuenow]="state()!.creditHealth.utilizationPct" aria-valuemin="0" aria-valuemax="100">
                  <div class="bai-progress-bar__fill" [class.bai-progress-bar__fill--warn]="state()!.creditHealth.utilizationPct > 30" [class.bai-progress-bar__fill--danger]="state()!.creditHealth.utilizationPct > 50" [style.width.%]="state()!.creditHealth.utilizationPct"></div>
                  <div class="bai-progress-bar__marker" style="left:30%"></div>
                  <div class="bai-progress-bar__marker" style="left:50%"></div>
                </div>
                <div class="flex justify-between text-xs text-gray-400">
                  <span>On-time payments: {{ state()!.creditHealth.onTimePaymentPct }}%</span>
                </div>
              </div>
            </div>

            <div class="md:col-span-2 flex flex-col gap-2">
              <span class="bai-fin-kicker">Recent changes</span>
              <ul class="flex flex-col gap-1" aria-label="Recent credit changes">
                @for (change of state()!.creditHealth.recentChanges; track change) {
                  <li class="flex items-start gap-1.5 text-xs text-gray-600">
                    <i class="pi pi-info-circle text-blue-400 mt-0.5 shrink-0"></i>
                    {{ change }}
                  </li>
                }
              </ul>
            </div>
          </div>
        }
      </section>

      <section class="bai-card bai-detail-card" aria-label="Liabilities and debt profile">
        <button
          type="button"
          class="bai-detail-toggle"
          (click)="toggleSection('debt')"
          [attr.aria-expanded]="debtDetailsOpen()"
        >
          <span class="bai-detail-toggle__title"><i class="pi pi-wallet mr-2"></i>Liabilities &amp; Debt Profile</span>
          <span class="bai-detail-toggle__meta">{{ formatUsd(totalDebt(state()!.debts)) }} total</span>
          <i class="pi bai-detail-toggle__chevron" [class.pi-chevron-down]="debtDetailsOpen()" [class.pi-chevron-right]="!debtDetailsOpen()"></i>
        </button>

        @if (debtDetailsOpen()) {
          <div class="mt-4 mb-2">
            <div class="bai-debt-bar" role="img" aria-label="Debt breakdown by type">
              @for (seg of debtSegments(state()!.debts); track seg.type) {
                <div class="bai-debt-bar__segment" [class]="'bai-debt-bar__segment--' + seg.type" [style.width.%]="seg.pct" [title]="debtTypeLabel(seg.type) + ': ' + seg.pct.toFixed(1) + '%'"></div>
              }
            </div>
            <div class="flex flex-wrap gap-x-4 gap-y-1 mt-2">
              @for (seg of debtSegments(state()!.debts); track seg.type) {
                <span class="flex items-center gap-1.5 text-xs text-gray-600">
                  <span class="bai-debt-legend" [class]="'bai-debt-legend--' + seg.type"></span>
                  {{ debtTypeLabel(seg.type) }} ({{ seg.pct.toFixed(0) }}%)
                </span>
              }
            </div>
          </div>

          <div class="overflow-x-auto mt-4">
            <table class="bai-actions-table bai-fin-debt-table text-sm" aria-label="Debt items">
              <thead>
                <tr>
                  <th baiColResize scope="col">Lender</th>
                  <th baiColResize scope="col">Type</th>
                  <th baiColResize scope="col" class="bai-cell-nowrap">Balance</th>
                  <th baiColResize scope="col" class="bai-cell-nowrap">APR</th>
                  <th baiColResize scope="col" class="bai-cell-nowrap">Min Payment</th>
                  <th scope="col" class="bai-cell-nowrap">Risk</th>
                  <th scope="col" class="bai-cell-nowrap">More</th>
                </tr>
              </thead>
              <tbody>
                @for (debt of state()!.debts; track trackDebt($index, debt)) {
                  <tr>
                    <td>{{ debt.lender }}</td>
                    <td>{{ debtTypeLabel(debt.type) }}</td>
                    <td class="bai-cell-nowrap">{{ formatUsd(debt.balance) }}</td>
                    <td class="bai-cell-nowrap">{{ debt.apr !== null ? (debt.apr | number:'1.2-2') + '%' : '—' }}</td>
                    <td class="bai-cell-nowrap">{{ debt.minPayment !== null ? formatUsd(debt.minPayment) + '/mo' : '—' }}</td>
                    <td class="bai-cell-nowrap">
                      <p-tag [value]="debt.risk | titlecase" [severity]="debtRiskSeverity(debt.risk)" styleClass="text-xs" />
                    </td>
                    <td class="bai-cell-nowrap">
                      <details class="bai-fin-kebab" (click)="$event.stopPropagation(); $any($event.target).closest('[class*=__action]') && $any($event.currentTarget).removeAttribute('open')">
                        <summary class="bai-fin-kebab__btn" aria-label="Debt item options">
                          <i class="pi pi-bars" aria-hidden="true"></i>
                        </summary>
                        <button
                          type="button"
                          class="bai-fin-kebab__action"
                          (click)="addDebtToCopilot(debt)"
                        >
                          Add to Copilot
                        </button>
                      </details>
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        }
      </section>

      <section class="bai-card bai-detail-card" aria-label="Utilization and affordability">
        <button
          type="button"
          class="bai-detail-toggle"
          (click)="toggleSection('affordability')"
          [attr.aria-expanded]="affordabilityDetailsOpen()"
        >
          <span class="bai-detail-toggle__title"><i class="pi pi-chart-pie mr-2"></i>Utilization &amp; Affordability</span>
          <span class="bai-detail-toggle__meta">{{ state()!.creditHealth.utilizationPct }}% util · {{ formatDti(state()!.dti) }} DTI</span>
          <i class="pi bai-detail-toggle__chevron" [class.pi-chevron-down]="affordabilityDetailsOpen()" [class.pi-chevron-right]="!affordabilityDetailsOpen()"></i>
        </button>

        @if (affordabilityDetailsOpen()) {
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div class="bai-card bai-subcard">
              <div class="bai-card__header">
                <h3 class="bai-card__title"><i class="pi pi-chart-pie mr-2"></i>Credit Utilization</h3>
              </div>
              <div class="mt-4 flex flex-col gap-3">
                <div class="flex items-end gap-2">
                  <span class="text-3xl font-bold text-gray-800">{{ state()!.creditHealth.utilizationPct }}%</span>
                  <span class="text-sm text-gray-500 mb-1">of revolving credit used</span>
                </div>
                <div class="bai-utilization-track" role="progressbar" [attr.aria-valuenow]="state()!.creditHealth.utilizationPct" aria-valuemin="0" aria-valuemax="100" [attr.aria-label]="'Credit utilization: ' + state()!.creditHealth.utilizationPct + '%'">
                  <div class="bai-utilization-fill" [class.bai-utilization-fill--warn]="state()!.creditHealth.utilizationPct > 30" [class.bai-utilization-fill--danger]="state()!.creditHealth.utilizationPct > 50" [style.width.%]="state()!.creditHealth.utilizationPct"></div>
                  <div class="bai-utilization-marker" style="left:30%" title="30% threshold"></div>
                  <div class="bai-utilization-marker" style="left:50%" title="50% threshold"></div>
                </div>
                @if (state()!.creditHealth.utilizationPct > 30) {
                  <p class="text-xs text-amber-700 bg-amber-50 rounded p-2">Reducing utilization below 30% could improve the credit score.</p>
                }
              </div>
            </div>

            <div class="bai-card bai-subcard">
              <div class="bai-card__header">
                <h3 class="bai-card__title"><i class="pi pi-chart-bar mr-2"></i>Debt-to-Income Ratio</h3>
              </div>
              <div class="mt-4 flex flex-col gap-3">
                <div class="flex items-center gap-3">
                  <span class="text-3xl font-bold text-gray-800">{{ formatDti(state()!.dti) }}</span>
                  <p-tag [value]="dtiRiskLabel(state()!.dti)" [severity]="dtiRiskSeverity(state()!.dti)" />
                </div>
                <div class="bai-utilization-track" role="progressbar" [attr.aria-valuenow]="state()!.dti * 100" aria-valuemin="0" aria-valuemax="100">
                  <div class="bai-utilization-fill" [class.bai-utilization-fill--warn]="state()!.dti > 0.36" [class.bai-utilization-fill--danger]="state()!.dti > 0.50" [style.width.%]="state()!.dti * 100"></div>
                  <div class="bai-utilization-marker" style="left:28%" title="28% low/moderate boundary"></div>
                  <div class="bai-utilization-marker" style="left:36%" title="36% conventional limit"></div>
                  <div class="bai-utilization-marker" style="left:50%" title="50% high-risk threshold"></div>
                </div>
                <dl class="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-gray-600 mt-1">
                  <dt class="text-gray-400">Monthly Income</dt>
                  <dd class="font-medium">{{ formatUsd(state()!.monthlyIncome) }}</dd>
                  <dt class="text-gray-400">Monthly Expenses</dt>
                  <dd class="font-medium">{{ formatUsd(state()!.monthlyExpenses) }}</dd>
                </dl>
              </div>
            </div>
          </div>
        }
      </section>

      <section class="bai-card bai-detail-card" aria-label="Income, assets, and emergency fund">
        <button
          type="button"
          class="bai-detail-toggle"
          (click)="toggleSection('assets')"
          [attr.aria-expanded]="assetsDetailsOpen()"
        >
          <span class="bai-detail-toggle__title"><i class="pi pi-dollar mr-2"></i>Income, Assets &amp; Financial Cushion</span>
          <span class="bai-detail-toggle__meta">{{ formatUsd(state()!.monthlyIncome) }}/mo income</span>
          <i class="pi bai-detail-toggle__chevron" [class.pi-chevron-down]="assetsDetailsOpen()" [class.pi-chevron-right]="!assetsDetailsOpen()"></i>
        </button>

        @if (assetsDetailsOpen()) {
          <div class="grid grid-cols-1 md:grid-cols-3 gap-6 mt-4">
            <div>
              <h3 class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Income Sources</h3>
              <ul class="flex flex-col gap-2" aria-label="Income sources">
                @for (src of state()!.incomeSources; track trackIncome($index, src)) {
                  <li class="flex justify-between text-sm">
                    <span class="text-gray-600">{{ src.label }}</span>
                    <span class="font-medium text-gray-800">{{ formatUsd(src.amount) }}/mo</span>
                  </li>
                }
                <li class="flex justify-between text-sm border-t border-gray-100 pt-2 mt-1">
                  <span class="font-semibold text-gray-700">Total</span>
                  <span class="font-bold text-gray-900">{{ formatUsd(state()!.monthlyIncome) }}/mo</span>
                </li>
              </ul>
            </div>

            <div>
              <h3 class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Assets Snapshot</h3>
              <ul class="flex flex-col gap-2" aria-label="Asset categories">
                @for (asset of state()!.assets; track trackAsset($index, asset)) {
                  <li class="flex justify-between text-sm">
                    <span class="text-gray-600">{{ asset.category }}</span>
                    <span class="font-medium text-gray-800">{{ formatUsd(asset.value) }}</span>
                  </li>
                }
                <li class="flex justify-between text-sm border-t border-gray-100 pt-2 mt-1">
                  <span class="font-semibold text-gray-700">Net Worth Est.</span>
                  <span class="font-bold text-gray-900">{{ formatUsd(state()!.assets.reduce(toSum, 0) - totalDebt(state()!.debts)) }}</span>
                </li>
              </ul>
            </div>

            <div>
              <h3 class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Emergency Fund</h3>
              <div class="flex flex-col gap-2">
                <div class="flex justify-between text-sm">
                  <span class="text-gray-600">Current savings</span>
                  <span class="font-medium text-gray-800">{{ formatUsd(state()!.emergencySavings) }}</span>
                </div>
                <div class="flex justify-between text-sm">
                  <span class="text-gray-600">Monthly expenses</span>
                  <span class="font-medium text-gray-800">{{ formatUsd(state()!.monthlyExpenses) }}</span>
                </div>
                <div class="flex justify-between text-sm">
                  <span class="text-gray-600">Coverage</span>
                  <span class="font-semibold" [class.text-green-600]="emergencyMonths(state()!) >= 3" [class.text-amber-600]="emergencyMonths(state()!) >= 1 && emergencyMonths(state()!) < 3" [class.text-red-600]="emergencyMonths(state()!) < 1">
                    {{ emergencyMonths(state()!).toFixed(1) }} / 6 months
                  </span>
                </div>
                <div class="bai-progress-bar mt-1" role="progressbar" [attr.aria-valuenow]="emergencyPct(state()!)" aria-valuemin="0" aria-valuemax="100" [attr.aria-label]="'Emergency fund: ' + emergencyMonths(state()!).toFixed(1) + ' months of coverage'">
                  <div class="bai-progress-bar__fill" [class.bai-progress-bar__fill--warn]="emergencyMonths(state()!) < 3" [class.bai-progress-bar__fill--danger]="emergencyMonths(state()!) < 1" [style.width.%]="emergencyPct(state()!)"></div>
                  <div class="bai-progress-bar__marker" style="left:50%" title="3-month target"></div>
                </div>
                <div class="flex justify-between text-xs text-gray-400">
                  <span>0 mo</span>
                  <span class="text-green-600 font-medium">3 mo target</span>
                  <span>6 mo ideal</span>
                </div>
              </div>
            </div>
          </div>
        }
      </section>

      <section class="bai-card bai-detail-card" aria-label="Bills and upcoming obligations">
        <button
          type="button"
          class="bai-detail-toggle"
          (click)="toggleSection('bills')"
          [attr.aria-expanded]="billsDetailsOpen()"
        >
          <span class="bai-detail-toggle__title"><i class="pi pi-calendar mr-2"></i>Bills &amp; Upcoming Obligations</span>
          <span class="bai-detail-toggle__meta">{{ urgentBillsCount(state()!) }} urgent</span>
          <i class="pi bai-detail-toggle__chevron" [class.pi-chevron-down]="billsDetailsOpen()" [class.pi-chevron-right]="!billsDetailsOpen()"></i>
        </button>

        @if (billsDetailsOpen()) {
          <div class="overflow-x-auto mt-4">
            <table class="bai-actions-table bai-fin-bills-table text-sm" aria-label="Bills and obligations">
              <thead>
                <tr>
                  <th baiColResize scope="col">Bill</th>
                  <th baiColResize scope="col">Category</th>
                  <th baiColResize scope="col" class="bai-cell-nowrap">Amount</th>
                  <th baiColResize scope="col" class="bai-cell-nowrap">Due Date</th>
                  <th scope="col" class="bai-cell-nowrap">Status</th>
                  <th scope="col" class="bai-cell-nowrap">More</th>
                </tr>
              </thead>
              <tbody>
                @for (bill of state()!.bills; track trackBill($index, bill)) {
                  <tr [class.bai-row--overdue]="bill.risk === 'overdue'" [class.bai-row--due-soon]="bill.risk === 'due_soon'">
                    <td class="font-medium">{{ bill.name }}</td>
                    <td>{{ bill.category }}</td>
                    <td class="bai-cell-nowrap">{{ formatUsd(bill.amount) }}</td>
                    <td class="bai-cell-nowrap">{{ bill.dueDate }}</td>
                    <td class="bai-cell-nowrap">
                      <p-tag [value]="billRiskLabel(bill.risk)" [severity]="billRiskSeverity(bill.risk)" styleClass="text-xs" />
                    </td>
                    <td class="bai-cell-nowrap">
                      <details class="bai-fin-kebab" (click)="$event.stopPropagation(); $any($event.target).closest('[class*=__action]') && $any($event.currentTarget).removeAttribute('open')">
                        <summary class="bai-fin-kebab__btn" aria-label="Bill options">
                          <i class="pi pi-bars" aria-hidden="true"></i>
                        </summary>
                        <button
                          type="button"
                          class="bai-fin-kebab__action"
                          (click)="addBillToCopilot(bill)"
                        >
                          Add to Copilot
                        </button>
                      </details>
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        }
      </section>
    </section>

  }

</div>
